import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const HIGHLIGHT_CLASS = 'skill-command-highlight';

export type SkillHighlightOptions = {
  /** Set of known skill slugs to highlight */
  slugs: Set<string>;
};

/**
 * Tiptap extension that highlights `/skill-name` tokens in the editor when
 * the slug matches a known skill. Applies an inline decoration (blue text
 * on light blue background) to make skill invocations visually distinct.
 */
export const SkillHighlightExtension = Extension.create<SkillHighlightOptions>({
  name: 'skillHighlight',

  addOptions() {
    return {
      slugs: new Set<string>(),
    };
  },

  addProseMirrorPlugins() {
    // Capture options ref so the decorations callback always reads the latest
    // slugs (options may update via setOptions when the skill list loads async).
    const options = this.options;

    return [
      new Plugin({
        key: new PluginKey('skillHighlight'),
        props: {
          decorations: (state) => {
            if (options.slugs.size === 0) return DecorationSet.empty;

            const decorations: Decoration[] = [];

            state.doc.descendants((node, pos) => {
              if (!node.isText || node.text == null) return;

              // Match /slug patterns preceded by whitespace or at start of text.
              // The lookbehind prevents false positives inside URLs like
              // https://example.com/weekly-report.
              const regex = /(?<=^|\s)\/[\w-]+/g;
              let match: RegExpExecArray | null;

              // eslint-disable-next-line no-cond-assign
              while ((match = regex.exec(node.text)) !== null) {
                const slug = match[0].slice(1); // remove leading /
                if (options.slugs.has(slug)) {
                  const from = pos + match.index;
                  const to = from + match[0].length;
                  decorations.push(
                    Decoration.inline(from, to, {
                      class: HIGHLIGHT_CLASS,
                    }),
                  );
                }
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});
